import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AttachmentGrid from './AttachmentGrid';
import AttachmentLikeHeart from './AttachmentLikeHeart';
import PresignedImage from '../common/PresignedImage';
import { addNotification } from '../../store/slices/uiSlice';
import { downloadAttachmentsAsZip, isDisplayableImage, isLinkAttachment } from '../../utils/fileUtils';
import { GoogleDriveIcon } from '../google/DriveFolderNameModal';

export const canGroupImageAttachments = (attachments = []) => {
  if (!attachments || attachments.length < 2) return false;
  return attachments.every(
    (att) => att?.fileType === 'IMAGE' || isDisplayableImage(att)
  );
};

/** True when compose has 2+ attachments (images, files, or mixed). */
export const canGroupComposeAttachments = (attachments = []) =>
  Array.isArray(attachments) && attachments.length >= 2;

const ImageStackIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.75}
      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const countAttachmentLabel = (attachments = []) => {
  const images = attachments.filter((a) => a?.fileType === 'IMAGE' || isDisplayableImage(a)).length;
  const total = attachments.length;
  if (images === total) {
    return `${total} image${total === 1 ? '' : 's'}`;
  }
  if (images === 0) {
    return `${total} file${total === 1 ? '' : 's'}`;
  }
  return `${total} items (${images} image${images === 1 ? '' : 's'})`;
};

const GroupThumbStack = ({ attachments = [], max = 3 }) => {
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const previewItems = attachments.slice(0, max);
  const extraCount = attachments.length - previewItems.length;

  return (
    <div className="flex -space-x-2 shrink-0">
      {previewItems.map((attachment, idx) => (
        <div
          key={attachment.id || attachment.fileUrl || idx}
          className="relative w-10 h-10 rounded-lg overflow-hidden border-2 border-white dark:border-gray-800 bg-gray-200 dark:bg-gray-700 shadow-sm"
        >
          {attachment?.fileType === 'IMAGE' || isDisplayableImage(attachment) ? (
            <PresignedImage
              fileKey={attachment.fileUrl}
              alt={attachment.fileName}
              originalName={attachment.fileName}
              className="w-full h-full object-cover"
              loadingClassName="w-full h-full bg-gray-200 dark:bg-gray-700 animate-pulse"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[10px] font-semibold text-gray-500">
              {idx + 1}
            </div>
          )}
          <AttachmentLikeHeart
            attachment={attachment}
            userId={currentUserId}
            size="sm"
            className="absolute bottom-0 right-0 scale-75 origin-bottom-right"
          />
        </div>
      ))}
      {extraCount > 0 && (
        <div className="w-10 h-10 rounded-lg border-2 border-white dark:border-gray-800 bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[11px] font-semibold text-gray-600 dark:text-gray-300 shadow-sm">
          +{extraCount}
        </div>
      )}
    </div>
  );
};

const AttachmentGroupBar = ({
  attachments = [],
  groupName = '',
  groupMode = 'none',
  existingGroupId = '',
  existingGroups = [],
  expanded = true,
  onExpandedChange,
  onViewImage,
  onDownload,
  onViewAll,
  onRemove,
  onArrange,
  onOpenGroup,
  onDownloadAll,
  onSaveToDrive,
  savingToDrive = false,
  driveUploadDone = 0,
  driveUploadTotal = 0,
  mode = 'compose',
  className = '',
}) => {
  const canGroupImages = canGroupImageAttachments(attachments);
  const canGroupCompose = canGroupComposeAttachments(attachments);
  const trimmedGroupName = (groupName || '').trim();
  const isDisplayNamedGroup = mode === 'display' && Boolean(trimmedGroupName);
  const canGroup = mode === 'compose' ? canGroupCompose : (canGroupImages || isDisplayNamedGroup);
  const selectedExisting = existingGroups.find((g) => g.id === existingGroupId);
  const collapsedLabel = isDisplayNamedGroup
    ? trimmedGroupName
    : (groupMode === 'existing' && selectedExisting
      ? selectedExisting.name
      : trimmedGroupName);
  const showCollapsed =
    mode === 'display' &&
    attachments.length > 0 &&
    Boolean(collapsedLabel) &&
    isDisplayNamedGroup;
  const itemLabel = countAttachmentLabel(attachments);
  const dispatch = useDispatch();
  const [downloadingAll, setDownloadingAll] = useState(false);
  const zipName = collapsedLabel || trimmedGroupName || 'attachments';
  const downloadableFiles = attachments.filter((att) => att?.fileUrl && !isLinkAttachment(att));
  const canSaveToDrive = mode === 'compose' && typeof onSaveToDrive === 'function' && downloadableFiles.length > 0;

  const SaveToDriveButton = ({ className = '' }) => {
    if (!canSaveToDrive) return null;
    return (
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onSaveToDrive();
        }}
        disabled={savingToDrive}
        className={`flex-shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-primary-700 dark:text-primary-300 bg-white dark:bg-gray-800 border border-primary-200 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/30 disabled:opacity-50 ${className}`}
        title="Upload these files to Google Drive and share view links"
      >
        <span className="inline-flex items-center gap-1.5">
          <GoogleDriveIcon className="w-4 h-4" />
          {savingToDrive && driveUploadTotal > 0
            ? `${driveUploadDone}/${driveUploadTotal} uploaded`
            : savingToDrive
              ? 'Saving to Drive…'
              : 'Save to Drive folder'}
        </span>
      </button>
    );
  };

  const handleDownloadAll = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!downloadableFiles.length || downloadingAll) return;
    setDownloadingAll(true);
    try {
      if (onDownloadAll) {
        await onDownloadAll(downloadableFiles, zipName);
      } else {
        await downloadAttachmentsAsZip(downloadableFiles, zipName);
      }
      dispatch(addNotification({
        type: 'success',
        message: `Downloading ${downloadableFiles.length} file${downloadableFiles.length === 1 ? '' : 's'}`,
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.message || 'Failed to download files',
      }));
    } finally {
      setDownloadingAll(false);
    }
  };

  const DownloadAllButton = ({ className = '' }) => {
    if (!downloadableFiles.length) return null;
    return (
      <button
        type="button"
        onClick={handleDownloadAll}
        disabled={downloadingAll}
        className={`flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors ${className}`}
        title={downloadingAll ? 'Preparing ZIP…' : 'Download all files'}
        aria-label={downloadingAll ? 'Preparing ZIP download' : `Download all files in ${zipName}`}
      >
        {downloadingAll ? (
          <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        )}
      </button>
    );
  };

  if (!attachments.length) return null;

  if (showCollapsed) {
    const openGroup = () => {
      if (onOpenGroup) {
        onOpenGroup(attachments, 0);
        return;
      }
      onExpandedChange?.(true);
    };
    return (
      <div
        className={`w-full max-w-[320px] flex items-center gap-1 pl-3 pr-1.5 py-2 rounded-xl border border-gray-200/90 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm hover:border-primary-300 hover:shadow transition-all ${className}`}
      >
        <button
          type="button"
          onClick={openGroup}
          className="flex-1 min-w-0 flex items-center gap-3 py-0.5 text-left"
          title="Open group"
          aria-label="Open attachment group"
        >
          <GroupThumbStack attachments={attachments} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" title={collapsedLabel}>
              {collapsedLabel}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{itemLabel}</p>
          </div>
        </button>
        <DownloadAllButton />
        <button
          type="button"
          onClick={openGroup}
          className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          title="Open group"
          aria-label="Open attachment group"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 3h6v6M21 3l-7 7M9 21H3v-6M3 21l7-7" />
          </svg>
        </button>
      </div>
    );
  }

  if (canGroup && mode === 'compose') {
    const previewItems = attachments.slice(0, 4);
    const extraCount = attachments.length - previewItems.length;
    const groupSummary =
      groupMode === 'existing' && selectedExisting
        ? selectedExisting.name
        : groupMode === 'new' && trimmedGroupName
          ? trimmedGroupName
          : groupMode === 'new'
            ? 'New group'
            : groupMode === 'existing'
              ? 'Choose a group'
              : 'Not grouped';

    return (
      <div className={`max-w-md rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/80 p-2.5 ${className}`}>
        <button
          type="button"
          onClick={() => onArrange?.()}
          className="w-full flex items-center gap-3 text-left rounded-lg hover:bg-white/70 dark:hover:bg-gray-700/50 transition-colors p-1"
        >
          <div className="flex -space-x-2 shrink-0">
            {previewItems.map((attachment, idx) => (
              <div
                key={attachment.id || attachment.fileUrl || idx}
                className="w-11 h-11 rounded-lg overflow-hidden border-2 border-white dark:border-gray-800 bg-gray-200 dark:bg-gray-700"
              >
                {attachment?.fileType === 'IMAGE' || isDisplayableImage(attachment) ? (
                  <PresignedImage
                    fileKey={attachment.fileUrl}
                    alt={attachment.fileName}
                    originalName={attachment.fileName}
                    className="w-full h-full object-cover"
                    loadingClassName="w-full h-full bg-gray-200 dark:bg-gray-700 animate-pulse"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] font-semibold text-gray-500">
                    {idx + 1}
                  </div>
                )}
              </div>
            ))}
            {extraCount > 0 && (
              <div className="w-11 h-11 rounded-lg border-2 border-white dark:border-gray-800 bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                +{extraCount}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{groupSummary}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {itemLabel} · tap to preview and reorder
            </p>
          </div>
          <span className="shrink-0 text-xs font-medium text-primary-600 dark:text-primary-400">
            Arrange
          </span>
        </button>
        {canSaveToDrive ? (
          <div className="mt-2 flex justify-end">
            <SaveToDriveButton />
          </div>
        ) : null}
      </div>
    );
  }

  if (isDisplayNamedGroup && expanded) {
    return (
      <div
        className={`max-w-[300px] rounded-xl border border-gray-200/90 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden shadow-sm ${className}`}
      >
        <div className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-100 dark:border-gray-700/80">
          <div className="flex-shrink-0 p-1.5 rounded-lg bg-primary-50 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300">
            <ImageStackIcon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" title={trimmedGroupName}>
              {trimmedGroupName}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{itemLabel}</p>
          </div>
          <DownloadAllButton />
          <button
            type="button"
            onClick={() => onExpandedChange?.(false)}
            className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Collapse group"
            aria-label="Collapse attachment group"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
        </div>
        <div className="p-1.5">
          <AttachmentGrid
            attachments={attachments}
            onViewImage={onViewImage}
            onDownload={onDownload}
            onViewAll={onViewAll}
            onRemove={onRemove}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {canSaveToDrive ? (
        <div className="mb-2 flex justify-end">
          <SaveToDriveButton />
        </div>
      ) : null}
      <AttachmentGrid
        attachments={attachments}
        onViewImage={onViewImage}
        onDownload={onDownload}
        onViewAll={onViewAll}
        onRemove={onRemove}
      />
    </div>
  );
};

export default AttachmentGroupBar;
